import { Box, CircularProgress, Typography, styled } from "@mui/material";
import Badge, { BadgeProps } from "@mui/material/Badge";
import { FC } from "react";
import { makeStyles } from "tss-react/mui";
import { palette } from "@/constants";
import { useSSTranslation } from "@/i18n/useSSTranslation";
import { SimplifiedLibrarySearch } from "./components/SimplifiedLibrarySearch";
import CustomLibraryCarousels from "./components/CustomLibraryCarousels";
import { IconCourseLibrary } from "@/icons/IconCourseLibrary";
import { useCourseLibrary } from "./hooks";

const SearchLabelBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
	"& .MuiBadge-badge": {
		right: "-20px",
		top: "-3px",
		border: `1px solid ${(theme.vars ?? theme).palette.primary.main}`,
		color: (theme.vars ?? theme).palette.text.primary
	}
}));

const useStyles = makeStyles()((theme) => ({
	library: {
		overflow: "visible"
	},
	searchWrapper: {
		marginBottom: "10px",
		display: "flex",
		boxSizing: "border-box",
		background: palette.black,
		flexDirection: "column",
		gap: "10px",
		width: "100%",
		["& [class*='searchBox-searchBox']"]: {
			display: "flex",
			justifyContent: "center",
			flexDirection: "column",
			paddingLeft: "15px"
		},
		["& [class*='librarySearch'] [class*='MuiTextField']"]: {
			background: palette.grey10,
			border: `1px solid ${palette.grey05}`,
			height: "52px",
			borderRadius: "8px",
			"input": {
				color: theme.palette.common.white
			},
			"& ::placeholder": {
				opacity: "1",
				color: theme.palette.common.white
			},
			"svg": {
				fill: theme.palette.common.white
			},
			display: "flex"
		},
		[theme.breakpoints.up("sm")]: {
			marginTop: "-80px",
			padding: "32px 60px 40px 60px",
			maxWidth: "1420px",
			borderRadius: "20px",
			marginLeft: "auto",
			marginRight: "auto"
		},
		[theme.breakpoints.down("sm")]: {
			padding: "30px 20px 40px 20px",
			marginLeft: "0",
			marginRight: "0",
			["& [class*='librarySearch'] [class*='MuiTextField']"]: {
				border: "none",
				height: "52px",
				display: "flex"
			}
		}
	},
	search: {
		margin: 0
	},
	searchLabel: {
		color: theme.palette.common.white,
		fontSize: "18px",
		fontWeight: 400
	},
	noneFoundContainer: {
		display: "flex",
		justifyContent: "center",
		color: theme.palette.text.primary,
		flexDirection: "column",
		gap: "15px",
		"svg": {
			width: "60px",
			height: "60px",
			"path": {
				fill: palette.greySolid
			}
		},
		"& div": {
			display: "flex",
			justifyContent: "center",
			flexDirection: "column"
		}
	},
	carouselsContainer: {
		display: "flex",
		flexDirection: "column",
		margin: "40px auto 90px auto",
		width: "100%",
		maxWidth: "1600px",
		boxSizing: "border-box",
		[theme.breakpoints.up("md")]: {
			padding: "0 40px"
		},
		[theme.breakpoints.between("sm", "md")]: {
			padding: "0 20px"
		},
		[theme.breakpoints.down("sm")]: {
			padding: "0 10px"
		}
	}
}));

interface CourseLibraryContentProps {
	className?: string;
}

export const CourseLibraryContent: FC<CourseLibraryContentProps> = ({ className }) => {
	const { t } = useSSTranslation();
	const { classes, cx } = useStyles();

	const {
		coursesData,
		totalCount,
		isLoading,
		searchValue,
		setSearchValue,
		categoryData,
		categoryHasMore,
		loadMoreDataForCategory,
		coursesCategories,
		categoryCounts
	} = useCourseLibrary();

	const DEFINED_CATEGORIES = ["Job Training", "Personal Development", "Professional Development", "Safety"];

	const getCategoryCount = (category: string): number | undefined => {
		if (!categoryCounts || Object.keys(categoryCounts).length === 0) return undefined;
		if (category !== "Others") return categoryCounts[category];
		let othersCount = 0;
		for (const [ct, count] of Object.entries(categoryCounts)) {
			if (!DEFINED_CATEGORIES.includes(ct)) othersCount += count;
		}
		return othersCount;
	};

	const getCategoryDisplayName = (category: string) => {
		const categoryMap: Record<string, string> = {
			"Job Training": "course_category_Job Training",
			"Personal Development": "course_category_Personal Development",
			"Professional Development": "course_category_Professional Development",
			"Safety": "course_category_Safety",
			"Others": "course_category_others"
		};

		const translationKey = categoryMap[category];
		return translationKey ? t(translationKey as any, category) : category;
	};

	return (
		<Box className={cx(classes.library, className)}>
			<Box className={classes.searchWrapper}>
				<Box display="flex" alignItems="center" gap={1} marginBottom="6px">
					<Typography variant="h5" className={classes.searchLabel}>
						{t("search_courses", "Search courses")}
					</Typography>
					<SearchLabelBadge badgeContent={totalCount} max={5000}/>
					{isLoading && <CircularProgress size={20} />}
				</Box>

				<SimplifiedLibrarySearch
					className={classes.search}
					value={searchValue.searchTerm || ""}
					onChange={(value) => setSearchValue({ ...searchValue, searchTerm: value })}
					textFieldProps={{
						inputProps: {
							placeholder: t("search_placeholder", "Course Title, Lesson Title, Author, Lesson Description")
						}
					}}
				/>
			</Box>
			<Box className={classes.carouselsContainer}>
				{!isLoading && coursesData && Array.isArray(coursesData) && coursesData.length === 0 ? (
					<Box className={classes.noneFoundContainer}>
						<IconCourseLibrary />
						<Box><Typography variant="h5">{t("no_courses_found", "No Courses Found")}</Typography></Box>
					</Box>
				) : (
					coursesData && Array.isArray(coursesData) &&
					coursesCategories
						.filter(category => {
							const categoryDataArray = categoryData[category] || [];
							return categoryDataArray.length > 0;
						})
						.map(category => {
							// Get courses for this category
							const searchCourses = categoryData[category] || [];

							// Enrich courses with additional data from API
							const enrichedCourses = searchCourses.map(searchCourse => ({
								...searchCourse,
								// Map API duration fields to expected format
								lessonDuration: searchCourse.totalDurationSeconds
									? Math.floor(searchCourse.totalDurationSeconds / 60)
									: 0,
								// Ensure all new API fields are available
								userProgressPercentage: searchCourse.userProgressPercentage || 0,
								totalDurationSeconds: searchCourse.totalDurationSeconds || 0,
								watchedDurationSeconds: searchCourse.watchedDurationSeconds || 0,
								remainingDurationSeconds: searchCourse.remainingDurationSeconds || 0,
								courseProgress: searchCourse.courseProgress || 0
							}));

							return (
								<CustomLibraryCarousels
									key={category}
									contentType={getCategoryDisplayName(category)}
									data={enrichedCourses}
									onLoadMore={loadMoreDataForCategory}
									hasMoreData={categoryHasMore[category] !== false}
									isLoading={isLoading}
									badgeCount={getCategoryCount(category)}
								/>
							);
						})
				)}
			</Box>
		</Box>
	);
};

